import { WidgetTemplateApi, type WidgetTemplateResponseType } from '../WidgetTemplateApi';
import { Api, ApiFilterBuilder } from '@automapa/svelte-automapa-template';
import { SettingsApi } from '../../../components/Settings/SettingsApi';
import { z } from 'zod';
import { get } from 'svelte/store';
import { _ } from 'svelte-i18n';

export class WidgetTemplateList {
    private api: Api;
    private user_id: number | null;
    private templates: WidgetTemplateResponseType[] = [];
    private currentTemplateId: number | null = null;
    private currentTemplateIdKey = 'dashboard_template_current_id';

    constructor(api: Api, user_id: number | null) {
        this.api = api;
        this.user_id = user_id;
    }

    async load(): Promise<void> {
        await this._loadCurrentTemplateId();
        await this._loadTemplates();
    }

    async save(): Promise<void> {
        for (const template of this.templates) {
            let newTemplate = {
                user_private: template.user_private,
                label: template.label,
            };
            try {
                await WidgetTemplateApi.modify(this.api, template.id, newTemplate);
            } catch (e) {
                await WidgetTemplateApi.create(this.api, newTemplate);
            }
        }
    }

    async _loadTemplates(): Promise<void> {
        this.templates = await WidgetTemplateApi.getAll(
            this.api,
            new ApiFilterBuilder(null),
            this.user_id
        );
    }

    getTemplates(): WidgetTemplateResponseType[] {
        return this.templates;
    }

    async createEmptyTemplate(): Promise<WidgetTemplateResponseType> {
        return WidgetTemplateApi.create(this.api, { label: get(_)('new_view'), user_private: true });
    }

    async removeTemplate(id: number): Promise<void> {
        await WidgetTemplateApi.remove(this.api, id);
        this.templates = this.templates.filter(t => t.id !== id);
    }

    async _loadCurrentTemplateId(): Promise<void> {
        let response: any = await SettingsApi.getClientSettings(
            this.api,
            this.currentTemplateIdKey,
            z.any()
        );
        this.currentTemplateId = z.coerce.number().nullable().catch(null).parse(response);
    }

    async saveCurrentTemplateId(): Promise<void> {
        await SettingsApi.setClientSettings(
            this.api,
            this.currentTemplateIdKey,
            this.currentTemplateId,
            z.number().nullable().catch(null)
        );
    }

    setTemplates(list: WidgetTemplateResponseType[]): this {
        this.templates = list;
        return this;
    }

    getTemplatesAsItems(): { id: string; label: string }[] {
        return this.templates.map(t => ({
            id: t.id.toString(),
            label: this._getFullTemplateLabel(t),
        }));
    }

    private _getFullTemplateLabel(t: WidgetTemplateResponseType): string {
        if (t.webuser_id === this.user_id) {
            // mój
            return t.label + (!t.user_private ? ' (udostępniony)' : '');
        } else {
            // kogoś innego
            return t.label + ' (od ' + t.webuser_name + ')';
        }
    }

    async setCurrentTemplateId(id: number, withSave: boolean = true): Promise<void> {
        this.currentTemplateId = id;
        if (withSave) {
            await this.saveCurrentTemplateId();
        }
    }

    getCurrentTemplateId(): number | null {
        if (this.currentTemplateId === null) {
            this.currentTemplateId = this.templates[0]?.id ?? null;
        }
        if (
            this.currentTemplateId !== null &&
            !this.templates.find(t => t.id === this.currentTemplateId)
        ) {
            this.currentTemplateId = this.templates[0]?.id ?? null;
        }
        return this.currentTemplateId;
    }

    async modifyTemplate(
        template: WidgetTemplateResponseType,
        withSave: boolean = true
    ): Promise<void> {
        const index = this.templates.findIndex(t => t.id === template.id);
        if (index !== -1) {
            this.templates[index] = template;
        }
        if (this.currentTemplateId === template.id) {
            await this.saveCurrentTemplateId();
        }
        if (withSave) {
            await WidgetTemplateApi.modify(this.api, template.id, {
                user_private: template.user_private,
                label: template.label,
            });
        }
    }

    async changeTemplateLabel(id: number, label: string, withSave: boolean = true): Promise<void> {
        const template = this.templates.find(t => t.id === id);
        if (template) {
            template.label = label;
            await this.modifyTemplate(template, withSave);
        }
    }

    getCurrentTemplate(): WidgetTemplateResponseType | null {
        return this.templates.find(t => t.id === this.getCurrentTemplateId()) ?? null;
    }

    public isReadOnly(): boolean {
        return this.user_id === null || this.getCurrentTemplate()?.webuser_id !== this.user_id;
    }
}
